import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { BookOpen, Wine, ChevronLeft, ChevronRight, GlassWater, Leaf, FlaskConical, ArrowLeft, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudyPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showImagePopup, setShowImagePopup] = useState(false);

  useEffect(() => {
    const fetchFlashcards = async () => {
      try {
        const res = await axios.get(`${API}/saved-tests/${testId}/flashcards`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load flashcards");
      } finally {
        setLoading(false);
      }
    };
    fetchFlashcards();
  }, [testId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <p className="text-[hsl(var(--muted-foreground))]">Loading flashcards...</p>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md w-full">
          <CardContent className="p-8 text-center">
            <BookOpen size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Load Flashcards</h2>
            <p className="text-[hsl(var(--muted-foreground))]">{error}</p>
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  const cocktails = data?.cocktails || [];
  const testName = data?.test_name || "Test";
  const cocktail = cocktails[currentIndex];

  if (!cocktail) return null;

  const prev = () => { setCurrentIndex((i) => Math.max(0, i - 1)); setShowImagePopup(false); };
  const next = () => { setCurrentIndex((i) => Math.min(cocktails.length - 1, i + 1)); setShowImagePopup(false); };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4 md:p-8 pb-20" data-testid="study-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="mb-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          data-testid="study-back-btn"
        >
          <ArrowLeft size={16} className="mr-1" /> Back to Dashboard
        </Button>
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="text-[hsl(var(--primary))]" size={28} />
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Study Flashcards</h1>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{testName}</p>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          Card {currentIndex + 1} of {cocktails.length}
        </p>
      </div>

      {/* Flashcard */}
      <div className={`mx-auto ${cocktail.image_url ? 'max-w-4xl' : 'max-w-2xl'}`}>
        <Card
          className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden"
          data-testid={`flashcard-${currentIndex}`}
        >
          <div className={`${cocktail.image_url ? 'md:flex' : ''}`}>
            {/* Cocktail Image - clickable thumbnail */}
            {cocktail.image_url && (
              <div
                className="md:w-[320px] md:shrink-0 bg-[hsl(var(--secondary))] relative cursor-pointer group"
                onClick={() => setShowImagePopup(true)}
                data-testid="flashcard-image"
              >
                <img
                  src={`${BACKEND}${cocktail.image_url}`}
                  alt={cocktail.name}
                  className="w-full h-48 md:h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-2">
                    <Maximize2 size={18} className="text-white" />
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Cocktail Name Header */}
              <div className="bg-gradient-to-r from-[hsl(var(--primary))]/15 to-transparent p-6 border-b border-[hsl(var(--border))]">
                <h2 className="text-2xl md:text-3xl font-bold font-serif italic text-[hsl(var(--primary))]">
                  {cocktail.name}
                </h2>
                <div className="flex gap-2 mt-2">
                  {cocktail.category && (
                    <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))]">
                      {cocktail.category}
                    </span>
                  )}
                  {cocktail.base_spirit && (
                    <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]">
                      {cocktail.base_spirit}
                    </span>
                  )}
                </div>
              </div>

              <CardContent className="p-6 space-y-6">
            {/* Ingredients */}
            {cocktail.ingredients && cocktail.ingredients.length > 0 && (
              <div>
                <h3 className="text-[hsl(var(--primary))] font-semibold mb-3 flex items-center gap-2">
                  <FlaskConical size={18} /> Ingredients
                </h3>
                <div className="space-y-2">
                  {cocktail.ingredients.map((ing, idx) => (
                    <div
                      key={`${ing.name || "ing"}-${idx}`}
                      className="flex items-start gap-3 p-2 rounded bg-[hsl(var(--secondary))]/50"
                    >
                      <span className="w-2 h-2 mt-2 rounded-full bg-[hsl(var(--primary))] shrink-0" />
                      <div>
                        <span className="font-mono text-sm text-[hsl(var(--muted-foreground))]">
                          {ing.measurement}
                        </span>
                        {ing.brand ? (
                          <span className="ml-2 font-medium">
                            {ing.brand}
                            <span className="text-[hsl(var(--muted-foreground))] ml-1">({ing.name})</span>
                          </span>
                        ) : (
                          <span className="ml-2 font-medium">{ing.name}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Glassware & Garnish */}
            <div className="grid grid-cols-2 gap-4">
              {cocktail.glassware && (
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]/50">
                  <h3 className="text-[hsl(var(--primary))] font-semibold text-sm mb-1 flex items-center gap-1.5">
                    <GlassWater size={14} /> Glassware
                  </h3>
                  <p className="text-sm">{cocktail.glassware}</p>
                </div>
              )}
              {cocktail.garnish && (
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]/50">
                  <h3 className="text-[hsl(var(--primary))] font-semibold text-sm mb-1 flex items-center gap-1.5">
                    <Leaf size={14} /> Garnish
                  </h3>
                  <p className="text-sm">{cocktail.garnish}</p>
                </div>
              )}
            </div>

            {/* Method */}
            {cocktail.method && (
              <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]/50">
                <h3 className="text-[hsl(var(--primary))] font-semibold text-sm mb-1 flex items-center gap-1.5">
                  <Wine size={14} /> Method
                </h3>
                <p className="text-sm">{cocktail.method}</p>
              </div>
            )}

            {/* Notes */}
            {cocktail.notes && (
              <div className="p-3 rounded-lg bg-[hsl(var(--primary))]/5 border border-[hsl(var(--primary))]/20">
                <p className="text-sm text-[hsl(var(--muted-foreground))] italic">{cocktail.notes}</p>
              </div>
            )}
          </CardContent>
            </div>
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6" data-testid="flashcard-nav">
          <Button
            variant="outline"
            onClick={prev}
            disabled={currentIndex === 0}
            className="border-[hsl(var(--border))]"
            data-testid="flashcard-prev-btn"
          >
            <ChevronLeft size={18} className="mr-1" /> Previous
          </Button>

          {/* Dot indicators */}
          <div className="flex gap-1.5 flex-wrap justify-center max-w-[60%]">
            {cocktails.map((c, idx) => (
              <button
                key={c.id || `dot-${idx}`}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  idx === currentIndex
                    ? "bg-[hsl(var(--primary))]"
                    : "bg-[hsl(var(--muted))] hover:bg-[hsl(var(--primary))]/50"
                }`}
                data-testid={`flashcard-dot-${idx}`}
              />
            ))}
          </div>

          <Button
            variant="outline"
            onClick={next}
            disabled={currentIndex === cocktails.length - 1}
            className="border-[hsl(var(--border))]"
            data-testid="flashcard-next-btn"
          >
            Next <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
      <Toaster position="top-center" richColors />

      {/* Full Image Popup */}
      {showImagePopup && cocktail.image_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setShowImagePopup(false)}
          data-testid="image-popup-overlay"
        >
          <button
            onClick={() => setShowImagePopup(false)}
            className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
            data-testid="image-popup-close"
          >
            <X size={24} className="text-white" />
          </button>
          <div
            className="relative max-w-lg w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${BACKEND}${cocktail.image_url}`}
              alt={cocktail.name}
              className="w-full max-h-[80vh] object-contain rounded-lg"
              data-testid="image-popup-img"
            />
            {/* Bottom overlay with cocktail name + branding */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent rounded-b-lg px-5 py-4">
              <h3 className="text-white text-xl font-bold font-serif italic">{cocktail.name}</h3>
              <p className="text-white/50 text-xs tracking-widest uppercase mt-1">Mixology-Hub</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
